<template>
  <v-dialog v-model="isOpen" max-width="800" persistent>
    <v-card class="beamer-club-agent-modal">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="agent-info">
            <div class="agent-avatar">
              <i class="material-icons-round">directions_car</i>
            </div>
            <div class="agent-details">
              <h3 class="agent-name">Alex</h3>
              <p class="agent-title">AutoSyncX™ Beamer Club Representative</p>
            </div>
          </div>
          <div class="header-actions">
            <!-- Mode Toggle -->
            <v-btn-toggle v-model="currentMode" mandatory class="mode-toggle">
              <v-btn value="chat" small>
                <i class="material-icons-round">chat</i>
                Chat
              </v-btn>
              <v-btn value="voice" small>
                <i class="material-icons-round">mic</i>
                Voice
              </v-btn>
            </v-btn-toggle>
            <!-- Close Button -->
            <v-btn icon @click="closeModal" class="close-btn">
              <i class="material-icons-round">close</i>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="modal-content">
        <!-- Chat Mode -->
        <div v-if="currentMode === 'chat'" class="chat-container">
          <div class="chat-messages" ref="chatMessages">
            <div v-for="message in messages" :key="message.id" class="message" :class="message.type">
              <div class="message-content">
                <div class="message-text" v-html="formatMessageText(message.text)"></div>
                <div class="message-time">{{ formatTime(message.timestamp) }}</div>
              </div>
            </div>
            <div v-if="isTyping" class="message agent typing">
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Chat Input -->
          <div class="chat-input-container">
            <v-text-field
              v-model="userInput"
              placeholder="Ask about the Beamer Club..."
              outlined
              dense
              hide-details
              @keyup.enter="sendMessage"
              :disabled="isTyping"
              class="chat-input"
            >
              <template v-slot:append>
                <v-btn icon @click="sendMessage" :disabled="!userInput.trim() || isTyping" color="primary">
                  <i class="material-icons-round">send</i>
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </div>

        <!-- Voice Mode -->
        <div v-if="currentMode === 'voice'" class="voice-container">
          <!-- Connection Status -->
          <v-alert v-if="connectionStatus.message" :type="connectionStatus.type" dense class="mb-4">
            {{ connectionStatus.message }}
          </v-alert>

          <!-- Voice Controls -->
          <div class="text-center mb-6">
            <div class="microphone-container">
              <v-btn
                :color="isVoiceCallActive ? 'green' : 'white'"
                icon
                size="x-large"
                dark
                :class="{ 'realtime-active': isVoiceCallActive }"
                @click="toggleVoiceConversation"
                :disabled="connectionStatus.type === 'error'"
                class="microphone-button"
              >
                <i class="material-icons-round" :style="{ color: isVoiceCallActive ? 'green' : '#333' }">{{ isVoiceCallActive ? 'mic' : 'mic_off' }}</i>
              </v-btn>

              <!-- Mute Button (only show when call is active) -->
              <v-btn
                v-if="isVoiceCallActive"
                :color="isMuted ? 'red' : 'grey'"
                icon
                dark
                size="large"
                @click="toggleMute"
                class="mute-button ml-4"
              >
                <i class="material-icons-round">{{ isMuted ? 'mic_off' : 'mic' }}</i>
              </v-btn>
            </div>


          </div>

          <!-- Voice Transcript -->
          <div v-if="voiceTranscript.length > 0" class="voice-transcript">
            <h4 class="mb-3">Conversation Transcript</h4>
            <div class="transcript-messages">
              <div v-for="(item, index) in voiceTranscript" :key="index" class="transcript-item" :class="item.role">
                <strong>{{ item.role === 'user' ? 'You' : 'Alex' }}:</strong>
                <span>{{ item.content }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { Conversation } from '@elevenlabs/client'

export default {
  name: 'BeamerClubAgentModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: this.value,
      currentMode: 'chat',
      messages: [
//         {
//           id: Date.now(),
//           type: 'agent',
//           text: `Hi! I’m Alex with AutoSync Beamer Club.
// I help members qualify for our BMW program — it’s designed around the 5, 6, and 7 Series models, whichever has the best wholesale-to-retail gap for your approval.
// The program covers your car payment and insurance through the AutoSync system, so you never come out of pocket.
// Would you like me to show you how it works?`,
//           timestamp: new Date()
//         }
      ],
      userInput: '',
      isTyping: false,
      conversationId: null,
      agentId: null,
      signedUrl: null,
      
      // Voice mode
      isVoiceCallActive: false,
      isSpeaking: false,
      isListening: false,
      isMuted: false,
      voiceTranscript: [],
      conversation: null,
      connectionStatus: {
        type: 'info',
        message: ''
      }
    }
  },
  computed: {
    voiceIcon() {
      if (this.isSpeaking) return 'record_voice_over'
      if (this.isListening) return 'hearing'
      if (this.isVoiceCallActive) return 'phone_in_talk'
      return 'phone'
    },
    voiceStatusText() {
      if (this.isSpeaking) return 'Alex is speaking...'
      if (this.isListening) return 'Listening...'
      if (this.isVoiceCallActive) return 'Call Active'
      return 'Ready to Call'
    },
    voiceDescription() {
      if (this.isVoiceCallActive) return 'You are now connected with Alex. Speak naturally!'
      return 'Click to start a voice conversation with Alex, your AutoSyncX™ Beamer Club representative.'
    }
  },
  watch: {
    value(newVal) {
      this.isOpen = newVal
      if (newVal) {
        this.initializeModal()
      }
    },
    isOpen(newVal) {
      this.$emit('input', newVal)
    },
    async currentMode(newMode) {
      if (newMode === 'chat' && !this.sessionId) {
        await this.initializeChat()
      } else if (newMode === 'voice' && !this.signedUrl) {
        await this.initializeVoice()
      }
    }
  },
  methods: {
    async initializeModal() {
      console.log('🚀 Initializing Beamer Club Agent Modal')
      
      // Initialize chat by default
      if (this.currentMode === 'chat') {
        await this.initializeChat()
      } else {
        await this.initializeVoice()
      }
    },

    async initializeChat() {
      console.log('💬 Initializing chat mode')

      // Prevent duplicate initialization
      if (this.sessionId) {
        console.log('⚠️ Chat already initialized with session:', this.sessionId)
        return
      }

      try {
        // Generate session ID
        this.sessionId = 'beamer_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
        console.log('📋 Session ID:', this.sessionId)

        // Initialize session with backend
        const response = await axios.post('/api/beamer-club/voice-agent/initialize-chat-session', {
          session_id: this.sessionId
        })

        if (response.data.flag === 1 && response.data.data) {
          this.sessionId = response.data.data.session_id
          this.conversationState = response.data.data.conversation_state

          // Add greeting message
          this.messages.push({
            id: Date.now(),
            type: 'agent',
            text: response.data.data.greeting_message,
            timestamp: new Date()
          })

          console.log('✅ Chat session initialized')
          console.log('📋 Session ID:', this.sessionId)
          console.log('📋 Conversation State:', this.conversationState)
        } else {
          throw new Error('Failed to initialize chat session')
        }
      } catch (error) {
        console.error('❌ Error initializing chat:', error)
        // Fallback: add default greeting message
        this.messages.push({
          id: Date.now(),
          type: 'agent',
          text: "Hi! I'm Alex with AutoSync Beamer Club.\nI help members qualify for our BMW program — it's designed around the 5, 6, and 7 Series models, whichever has the best wholesale-to-retail gap for your approval.\nThe program covers your car payment and insurance through the AutoSync system, so you never come out of pocket.\nWould you like me to show you how it works?",
          timestamp: new Date()
        })
        this.sessionId = 'beamer_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      }

      this.scrollToBottom()
    },



    async initializeVoice() {
      console.log('🎤 Initializing voice mode')
      console.log('📍 API URL: /api/beamer-club/voice-agent/create-conversation')

      this.connectionStatus = {
        type: 'info',
        message: 'Connecting...'
      }

      try {
        const response = await axios.post('/api/beamer-club/voice-agent/create-conversation')

        console.log('📋 Full API Response:', response.data)

        if (response.data.flag === 1 && response.data.data) {
          this.agentId = response.data.data.agent_id
          this.signedUrl = response.data.data.signed_url

          console.log('✅ Voice initialized with Beamer Club Agent:')
          console.log('📋 Agent ID:', this.agentId)
          console.log('📋 Signed URL:', this.signedUrl)

          if (!this.signedUrl) {
            throw new Error('No signed URL received from server')
          }

          this.connectionStatus = {
            type: 'success',
            message: 'Connected to Alex from AutoSyncX™ Beamer Club'
          }

          console.log('✅ Voice agent ready - microphone will be initialized when user clicks button')

        } else {
          console.error('❌ API returned error:', response.data)
          throw new Error(response.data.message || 'Voice agent not available')
        }
      } catch (error) {
        console.error('❌ Error initializing voice:', error)
        console.error('❌ Error response:', error.response?.data)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to initialize voice agent'
        this.connectionStatus = {
          type: 'error',
          message: errorMessage
        }
        this.$toast.error(errorMessage)
      }
    },

    async sendMessage() {
      if (!this.userInput.trim() || this.isLoading) return

      // Ensure session is initialized
      if (!this.sessionId) {
        console.warn('⚠️ Session not initialized, initializing now...')
        await this.initializeChat()
        if (!this.sessionId) {
          this.$toast.error('Failed to initialize chat session. Please refresh and try again.')
          return
        }
      }

      const userMessage = {
        id: Date.now(),
        type: 'user',
        text: this.userInput.trim(),
        timestamp: new Date()
      }

      this.messages.push(userMessage)
      const messageText = this.userInput.trim()
      this.userInput = ''
      this.isTyping = true
      this.isLoading = true

      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        // Send text message through backend API with session ID
        console.log('📤 Sending message to backend API:', messageText)
        console.log('📋 Session ID:', this.sessionId)

        // Get tracking ID from localStorage if exists
        const trackingId = localStorage.getItem('referral_tracking_id')

        const response = await axios.post('/api/beamer-club/voice-agent/send-chat-message', {
          message: messageText,
          session_id: this.sessionId,
          tracking_id: trackingId
        })

        console.log('� API Response:', response.data)

        if (response.data.flag === 1 && response.data.data && response.data.data.response) {
          // Update conversation state
          this.conversationState = response.data.data.conversation_state
          this.collectedData = response.data.data.collected_data || {}
          this.isComplete = response.data.data.is_complete || false

          console.log('📋 Conversation State:', this.conversationState)
          console.log('📋 Collected Data:', this.collectedData)
          console.log('📋 Is Complete:', this.isComplete)

          if (response.data.data.lead_created) {
            console.log('✅ Lead created successfully!')
            this.$toast.success('Your information has been saved!')
          }

          // Add agent response
          const agentMessage = {
            id: Date.now() + 1,
            type: 'agent',
            text: response.data.data.response,
            timestamp: new Date()
          }
          this.messages.push(agentMessage)

          this.$nextTick(() => {
            this.scrollToBottom()
          })
        } else {
          throw new Error(response.data.message || 'No response from agent')
        }
      } catch (error) {
        console.error('❌ Error sending message:', error)
        console.error('❌ Error details:', error.message, error.response?.data)

        // Fallback to generated response on error
        const agentMessage = {
          id: Date.now() + 1,
          type: 'agent',
          text: this.generateResponse(messageText),
          timestamp: new Date()
        }
        this.messages.push(agentMessage)

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } finally {
        this.isTyping = false
        this.isLoading = false
      }
    },

    generateResponse(userMessage) {
      // Simple response logic - in production, this would call the actual AI API
      const lowerMessage = userMessage.toLowerCase()
      
      if (lowerMessage.includes('bmw') || lowerMessage.includes('model')) {
        return "Great question! The Beamer Club has access to a wide range of BMW models including the 3 Series, 5 Series, X3, X5, and more. What specific model interests you?"
      } else if (lowerMessage.includes('price') || lowerMessage.includes('cost')) {
        return "Beamer Club members get exclusive pricing on all BMW vehicles. The exact price depends on the model and features. Would you like me to show you our current inventory?"
      } else if (lowerMessage.includes('join') || lowerMessage.includes('sign up')) {
        return "Joining the Beamer Club is easy! You'll get instant access to exclusive BMW inventory, special pricing, and our community of BMW enthusiasts. Ready to get started?"
      } else {
        return "I'd be happy to help you with that! The Beamer Club offers exclusive access to premium BMW vehicles and a community of enthusiasts. What would you like to know more about?"
      }
    },

    async toggleVoiceConversation() {
      if (this.isVoiceCallActive) {
        await this.endVoiceCall()
      } else {
        await this.startVoiceCall()
      }
    },

    async startVoiceCall() {
      console.log('📞 Starting voice call...')
      
      if (!this.signedUrl) {
        console.error('❌ No signed URL available')
        this.$toast.error('Voice agent not ready. Please try again.')
        return
      }

      try {
        console.log('🎙️ Initializing ElevenLabs conversation...')

        // Start ElevenLabs conversation using the official SDK
        this.conversation = await Conversation.startSession({
          signedUrl: this.signedUrl,
          onConnect: () => {
            console.log('✅ ElevenLabs conversation connected')
            this.isVoiceCallActive = true
            this.connectionStatus = {
              type: 'success',
              message: 'Real-time conversation active - speak naturally!'
            }
            this.$toast.success('Voice conversation started! You can now talk with Alex.')
          },
          onDisconnect: () => {
            console.log('🔌 ElevenLabs conversation disconnected')
            this.isVoiceCallActive = false
            this.connectionStatus = {
              type: 'info',
              message: 'Conversation ended'
            }
          },
          onMessage: (message) => {
            console.log('📨 Message received:', message)

            // Add to transcript
            if (message.message && message.message.content) {
              this.voiceTranscript.push({
                role: message.source === 'user' ? 'user' : 'agent',
                content: message.message.content
              })
            }
          },
          onError: (error) => {
            console.error('❌ ElevenLabs error:', error)
            this.connectionStatus = {
              type: 'error',
              message: 'Connection error. Please try again.'
            }
            this.isVoiceCallActive = false
          }
        })

        console.log('✅ Voice call started successfully')

      } catch (error) {
        console.error('❌ Error starting voice call:', error)
        this.$toast.error('Failed to start voice call: ' + error.message)
        this.isVoiceCallActive = false
        this.connectionStatus = {
          type: 'error',
          message: 'Failed to start voice call'
        }
      }
    },

    async endVoiceCall() {
      console.log('📴 Ending voice call...')

      try {
        if (this.conversation) {
          await this.conversation.endSession()
          this.conversation = null
        }

        this.isVoiceCallActive = false
        this.isSpeaking = false
        this.isListening = false
        this.isMuted = false

        this.connectionStatus = {
          type: 'info',
          message: 'Voice conversation ended'
        }

        console.log('✅ Voice call ended successfully')
        this.$toast.info('Voice conversation ended')

      } catch (error) {
        console.error('❌ Error ending voice call:', error)
        this.isVoiceCallActive = false
        this.isMuted = false
      }
    },

    async toggleMute() {
      if (!this.conversation || !this.isVoiceCallActive) {
        console.warn('⚠️ No active conversation to mute')
        return
      }

      try {
        if (this.isMuted) {
          // Unmute - resume audio input
          console.log('🎤 Unmuting microphone...')
          await this.conversation.startAudioInput()
          this.isMuted = false
          this.$toast.info('Microphone unmuted')
        } else {
          // Mute - stop audio input
          console.log('🔇 Muting microphone...')
          await this.conversation.stopAudioInput()
          this.isMuted = true
          this.$toast.info('Microphone muted')
        }
      } catch (error) {
        console.error('❌ Error toggling mute:', error)
        // Fallback: try to toggle the mute state anyway
        this.isMuted = !this.isMuted
        this.$toast.info(this.isMuted ? 'Microphone muted' : 'Microphone unmuted')
      }
    },

    closeModal() {
      // End voice call if active
      if (this.isVoiceCallActive) {
        this.endVoiceCall()
      }

      this.isOpen = false
      this.$emit('close')
    },

    formatTime(timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    },

    formatMessageText(text) {
      // Convert line breaks to <br> tags
      let formatted = text.replace(/\n/g, '<br>')

      // Convert URLs to clickable links
      const urlRegex = /(https?:\/\/[^\s]+)/g
      formatted = formatted.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="message-link">$1</a>')

      // Convert emojis and special characters properly
      return formatted
    },

    scrollToBottom() {
      if (this.$refs.chatMessages) {
        this.$nextTick(() => {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
        })
      }
    }
  },

  mounted() {
    // Initialize chat session when component mounts (only if not already initialized)
    if (this.isOpen && this.currentMode === 'chat' && !this.sessionId) {
      this.initializeChat()
    }
  },

  beforeDestroy() {
    // Clean up voice call if active
    if (this.isVoiceCallActive) {
      this.endVoiceCall()
    }
  }
}
</script>

<style scoped>
.beamer-club-agent-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 20px 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-avatar i {
  font-size: 32px;
  color: white;
}

.agent-details {
  display: flex;
  flex-direction: column;
}

.agent-name {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.agent-title {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mode-toggle {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
}

.modal-content {
  padding: 0;
  height: 500px;
  overflow: hidden;
}

/* Chat Mode Styles */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f5f5f5;
}

.message {
  display: flex;
  margin-bottom: 16px;
}

.message.user {
  justify-content: flex-end;
}

.message.agent {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
}

.message.user .message-content {
  background: #1976d2;
  color: white;
}

.message.agent .message-content {
  background: white;
  color: #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.message-text {
  margin-bottom: 4px;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
}

.typing-indicator {
  display: flex;
  gap: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #999;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

.message-link {
  color: #1976d2;
  text-decoration: underline;
  word-break: break-all;
}

.message.user .message-link {
  color: #bbdefb;
}

.message-link:hover {
  color: #1565c0;
  text-decoration: none;
}

.chat-input-container {
  padding: 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

/* Voice Mode Styles */
.voice-container {
  padding: 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.microphone-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
}

.microphone-button {
  width: 120px !important;
  height: 120px !important;
  border-radius: 50% !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.microphone-button i {
  font-size: 48px;
}

.microphone-button.realtime-active {
  animation: pulse 2s infinite;
}

.mute-button {
  width: 60px !important;
  height: 60px !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.mute-button i {
  font-size: 28px;
  color: red;
}

.mute-button:hover {
  transform: scale(1.05);
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(76, 175, 80, 0);
  }
}

.voice-transcript {
  margin-top: 30px;
  max-height: 250px;
  overflow-y: auto;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.transcript-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transcript-item {
  padding: 8px 12px;
  border-radius: 8px;
  background: white;
}

.transcript-item.user {
  background: #e3f2fd;
}

.transcript-item.agent {
  background: #f5f5f5;
}

.transcript-item strong {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: #666;
}
</style>


